import React, { useEffect, useRef, useState } from 'react';
import Image from '@/components/common/Image';
import { useRevealAnimation } from '@/hooks/useRevealAnimation';
import { ImageLightbox } from './ImageLightbox';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';

// Import the lightbox styles
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

export const BlogImage: React.FC<{
  content: {
    src: string;
    alt: string;
    caption?: string;
    isGallery?: boolean;
    galleryId?: string;
  };
  galleryGroups?: Record<string, any[]>;
}> = ({ content, galleryGroups = {} }) => {
  const { ref, isVisible } = useRevealAnimation();
  const [imageError, setImageError] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(16 / 9); // Default aspect ratio
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Calculate and set container height based on width and aspect ratio
  useEffect(() => {
    if (containerRef.current) {
      const updateHeight = () => {
        const width = containerRef.current?.clientWidth || 0;
        const height = width / aspectRatio;
        if (containerRef.current) {
          containerRef.current.style.height = `${height}px`;
        }
      };

      // Set initial height
      updateHeight();

      // Update height on window resize
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }
  }, [aspectRatio, containerRef]);

  // Preload image to get its natural aspect ratio
  useEffect(() => {
    if (!imageError) {
      const img = new window.Image();
      img.onload = () => {
        setAspectRatio(img.naturalWidth / img.naturalHeight);
      };
      img.onerror = () => setImageError(true);
      img.src = content.src;
    }
  }, [content.src, imageError]);

  // Get the current index in the gallery
  const gallerySlides =
    content.isGallery && content.galleryId
      ? galleryGroups[content.galleryId] || []
      : [];
  const currentIndex = gallerySlides.findIndex(
    (slide) => slide.src === content.src,
  );

  return (
    <figure
      ref={ref}
      className={`mb-8 transform transition-all duration-700 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}
      data-gallery-id={content.galleryId}
    >
      <div
        ref={containerRef}
        className="relative w-full rounded-lg overflow-hidden cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        {imageError ? (
          <div className="w-full h-full flex items-center justify-center bg-zinc-100 dark:bg-zinc-800">
            <div className="text-center p-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 mx-auto text-zinc-400 mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-zinc-500 dark:text-zinc-400">
                Image could not be loaded
              </p>
            </div>
          </div>
        ) : (
          <Image
            src={content.src}
            alt={content.alt}
            fill
            style={{ objectFit: 'cover' }}
            className="transition-transform duration-500 hover:scale-105"
            onError={() => setImageError(true)}
          />
        )}
      </div>
      {content.caption && (
        <figcaption className="text-center text-sm text-zinc-500 dark:text-zinc-400 mt-2">
          {content.caption}
        </figcaption>
      )}

      {isOpen &&
        !imageError &&
        (content.isGallery && content.galleryId && gallerySlides.length > 0 ? (
          <Lightbox
            open={true}
            close={() => setIsOpen(false)}
            slides={gallerySlides}
            index={currentIndex >= 0 ? currentIndex : 0}
            plugins={[Zoom, Captions, Fullscreen, Thumbnails]}
            captions={{ descriptionTextAlign: 'center' }}
            animation={{ fade: 300 }}
          />
        ) : (
          <ImageLightbox
            src={content.src}
            alt={content.alt}
            caption={content.caption}
            onClose={() => setIsOpen(false)}
          />
        ))}
    </figure>
  );
};
